import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import PromptSheet, {
  EXIT_MS, usePromptClose, CloseButton, DragHandle, AppIconTile, PromptCopy, PromptActions,
} from './PromptSheet';
import IosInstallDemo, { useIosInstallSteps, ACTIVE_ROW, STEP_LABELS } from './IosInstallDemo';

const COOLDOWN_KEY = 'pr:install_prompt_dismissed_at';
const COOLDOWN_DAYS = 14;
const SHOW_DELAY_MS = 30 * 1000;
// The iOS card slides up a beat after the trigger, so it never lands mid-scroll.
const IOS_OPEN_DELAY_MS = 2000;

function isStandalone() {
  // Inside the Capacitor native shell we ARE the installed app —
  // never prompt to install.
  try { if (Capacitor.isNativePlatform()) return true; } catch {}
  // Chrome / Edge / Samsung set the standalone media query.
  // iOS Safari uses navigator.standalone (non-standard).
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.navigator?.standalone === true) return true;
  return false;
}

function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Chrome on iOS reports CriOS in UA — it CAN'T add to home screen, only Safari can.
  // Treat Chrome-iOS the same as Safari-iOS for prompt purposes (instructions still
  // point to Safari since that's the only path).
  const webkit = /WebKit/.test(ua) && !/Edge|Edg\//.test(ua);
  return iOS && webkit;
}

function inCooldown() {
  try {
    const at = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
    if (!at) return false;
    const ageMs = Date.now() - at;
    return ageMs < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function setCooldown() {
  try { localStorage.setItem(COOLDOWN_KEY, String(Date.now())); } catch {}
}

/**
 * InstallAppPrompt — invites the user to install Portal as a PWA.
 *
 * Behavior:
 *   • Skips entirely if already running as installed PWA (display-mode standalone
 *     or navigator.standalone), or if dismissed inside the 14-day cooldown.
 *   • Android Chrome / Edge / Samsung: listens for `beforeinstallprompt`, defers
 *     it, and shows a white bottom sheet after 30s. INSTALL triggers the native
 *     picker via the deferred event.
 *   • iOS Safari (no `beforeinstallprompt` support): a compact card floats above
 *     Safari's toolbar 2s after the 30s mark, its tail pointing at the ••• button,
 *     with a looping demo of the iOS 26 path (••• → Share → scroll → Add to Home
 *     Screen → Add) and the step list lit in sync. GOT IT dismisses.
 *   • Either dismissal stamps the cooldown so we don't re-pester.
 * Design: design_handoff_install_notify_icons §1a / §1b.
 */
export default function InstallAppPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState(null); // 'android' | 'ios'
  const [closing, close] = usePromptClose(variant === 'ios' ? EXIT_MS.card : EXIT_MS.up, () => setShow(false));

  useEffect(() => {
    if (isStandalone() || inCooldown()) return;

    let timer;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      // Even if the event fires sooner, hold the visible prompt until the 30s mark
      // so we don't blow up first impressions on a fresh visit.
      if (!timer) {
        timer = setTimeout(() => { setVariant('android'); setShow(true); }, SHOW_DELAY_MS);
      } else {
        // Timer was already armed for iOS path — keep it; the android variant
        // will swap in when the event lands.
      }
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS path — no event ever fires, so just schedule the card after the delay
    // (plus its own 2s settle). Android Chrome will swap to the native-button
    // variant via onPrompt above before this timer fires (typically within a
    // few seconds).
    if (isIosSafari()) {
      timer = setTimeout(() => { setVariant('ios'); setShow(true); }, SHOW_DELAY_MS + IOS_OPEN_DELAY_MS);
    }

    const onInstalled = () => {
      // User installed via OS-level UI (e.g. address-bar button on desktop) —
      // hide our prompt + cooldown so we don't ask again.
      setShow(false);
      setCooldown();
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!show) return null;

  const dismiss = () => { if (closing) return; setCooldown(); close(); };

  const onInstallClick = async () => {
    if (!deferredEvent) { dismiss(); return; }
    try {
      deferredEvent.prompt();
      await deferredEvent.userChoice; // resolves with { outcome: 'accepted' | 'dismissed' }
    } catch {
      // User backed out or the browser ate the event — either way, cooldown.
    } finally {
      setCooldown();
      close();
    }
  };

  if (variant === 'ios') return <IosInstallCard closing={closing} onDismiss={dismiss} />;

  return (
    <PromptSheet direction="up" closing={closing} onDismiss={dismiss} label="Add Portal to your home screen">
      <DragHandle style={{ padding: '10px 0 0' }} />
      <CloseButton onClick={dismiss} style={{ top: 16, right: 16 }} />
      <div className="pr-prompt-rise" style={{ padding: '28px 24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 18 }}>
        <AppIconTile />
        <PromptCopy
          eyebrow="INSTALL"
          title="ADD PORTAL TO YOUR HOME SCREEN"
          body="Faster, fullscreen, one tap from your home screen."
        />
      </div>
      <PromptActions primaryLabel="INSTALL" onPrimary={onInstallClick} onSecondary={dismiss} />
    </PromptSheet>
  );
}

/**
 * The iOS 26 Safari card. No scrim — the page stays usable, and the card sits
 * just above Safari's bottom toolbar with its tail at the ••• button. The demo
 * loops while the card is open and stops the moment it starts closing.
 */
function IosInstallCard({ closing, onDismiss }) {
  const step = useIosInstallSteps(!closing);
  const active = ACTIVE_ROW[step];
  return (
    <div
      role="dialog"
      aria-label="Add Portal to your home screen"
      className={`fixed z-50 ${closing ? 'pr-card-down' : 'pr-card-up'}`}
      style={{
        left: 10, right: 10, bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', maxWidth: 420, marginLeft: 'auto',
        background: '#fff', border: '2px solid #000', boxShadow: '4px 4px 0 0 #000',
        fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: '#000',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(0,1fr)', gap: 14, padding: 12 }}>
        <IosInstallDemo step={step} />
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 2 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.22em', color: '#6B7280' }}>INSTALL</span>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '.06em', lineHeight: 1.25, textWrap: 'balance' }}>ADD PORTAL TO YOUR HOME SCREEN</h2>
            </div>
            <CloseButton onClick={onDismiss} size={30} style={{ position: 'static', flexShrink: 0 }} />
          </div>
          <ol data-testid="ios-install-steps" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', borderTop: '2px solid #000' }}>
            {STEP_LABELS.map((label, i) => (
              <li
                key={label}
                aria-current={i === active ? 'step' : undefined}
                style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid #E5E5E5', color: i === active ? '#000' : '#A3A3A3', transition: 'color .2s' }}
              >
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em' }}>{`0${i + 1}`}</span>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', lineHeight: 1.4 }}>{label}</span>
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={onDismiss}
            className="pr-prompt-btn pr-prompt-invert-dark"
            style={{ marginTop: 'auto', height: 40, border: '2px solid #000', background: '#000', color: '#fff', font: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.18em', cursor: 'pointer' }}
          >
            GOT IT
          </button>
        </div>
      </div>
      {/* The tail, pointing at Safari's ••• button. */}
      <span aria-hidden="true" style={{ position: 'absolute', right: 28, bottom: -12, width: 18, height: 18, background: '#fff', borderRight: '2px solid #000', borderBottom: '2px solid #000', transform: 'rotate(45deg)' }} />
    </div>
  );
}
